import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, Form, Input, Result, Spin, Typography } from "antd";
import { Link, useParams } from "react-router";

import { api } from "../api";
import { AuthCard } from "./auth-card";

const MIN_PASSWORD_LENGTH = 12;

type Values = { password: string; confirm: string };

/** /invite/:token — the invited staff member sets a password, then signs in. */
export function InvitePage() {
  const { token = "" } = useParams();
  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api<{ name: string; email: string }>(`/api/office/invitations/${token}`),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: (password: string) =>
      api(`/api/office/invitations/${token}/accept`, { method: "POST", body: { password } }),
  });

  if (invitation.isPending) {
    return (
      <AuthCard title="Davet">
        <Spin />
      </AuthCard>
    );
  }
  if (invitation.isError) {
    return (
      <AuthCard title="Davet">
        <Result status="warning" subTitle={invitation.error.message} />
      </AuthCard>
    );
  }
  if (accept.isSuccess) {
    return (
      <AuthCard title="Şifren kaydedildi">
        <Typography.Paragraph>
          Giriş yaptıktan sonra authenticator uygulamanla iki adımlı doğrulamayı kuracaksın.
        </Typography.Paragraph>
        <Link to="/login">
          <Button type="primary" block>
            Giriş yap
          </Button>
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={`Hoş geldin, ${invitation.data.name}`}>
      <Typography.Paragraph type="secondary">
        {invitation.data.email} hesabın için bir şifre belirle.
      </Typography.Paragraph>
      {accept.isError ? (
        <Alert type="error" showIcon message={accept.error.message} style={{ marginBottom: 16 }} />
      ) : null}
      <Form<Values>
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => accept.mutate(values.password)}
      >
        <Form.Item
          label="Şifre"
          name="password"
          extra={`En az ${MIN_PASSWORD_LENGTH} karakter.`}
          rules={[
            { required: true, message: "Şifre belirle." },
            {
              min: MIN_PASSWORD_LENGTH,
              message: `Şifre en az ${MIN_PASSWORD_LENGTH} karakter olmalı.`,
            },
            { max: 128, message: "Şifre en fazla 128 karakter olabilir." },
          ]}
        >
          <Input.Password autoComplete="new-password" autoFocus />
        </Form.Item>
        <Form.Item
          label="Şifre (tekrar)"
          name="confirm"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Şifreyi tekrar gir." },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || value === getFieldValue("password")
                  ? Promise.resolve()
                  : Promise.reject(new Error("Şifreler aynı değil.")),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={accept.isPending}>
          Şifreyi kaydet
        </Button>
      </Form>
    </AuthCard>
  );
}
